import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Calendar, AlertTriangle, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { usePeriod } from "@/hooks/usePeriod";

// Section labels in Italian
const SECTION_LABELS = {
  expenses: "Spese",
  habits: "Abitudini",
  moods: "Umore",
  appointments: "Appuntamenti",
  diary: "Diario",
  needs: "Bisogni",
  life_tasks: "Compiti",
  reminders: "Promemoria"
};

// Delete day data component
export const DeleteDayButton = ({ section, date, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await api.delete(`/settings/delete-day/${section}/${date}`);
      toast.success(`Eliminati ${response.data.deleted_count} elementi del ${new Date(date).toLocaleDateString("it-IT")}`);
      setOpen(false);
      if (onDeleted) onDeleted();
    } catch (error) {
      toast.error("Errore nell'eliminazione: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = new Date(date).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="delete-day-btn">
          <Trash2 className="w-4 h-4 mr-1" /> Cancella giorno
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Cancella dati del giorno
          </DialogTitle>
          <DialogDescription>
            Stai per eliminare <strong>definitivamente</strong> tutti i dati di <strong>{SECTION_LABELS[section]}</strong> del giorno:
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm text-center">
          <p className="font-medium capitalize">{formattedDate}</p>
        </div>
        
        <p className="text-sm text-muted-foreground">
          ⚠️ Questa azione è <strong>irreversibile</strong>. I dati verranno eliminati permanentemente.
        </p>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
            Annulla
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete} 
            disabled={loading}
            className="rounded-sm"
            data-testid="confirm-delete-day-btn"
          >
            {loading ? "Eliminazione..." : "Elimina definitivamente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// New period for section component
export const NewPeriodButton = ({ section, onPeriodSet }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateSectionPeriod, refetchSettings } = usePeriod();

  const handleSetPeriod = async () => {
    setLoading(true);
    const today = new Date().toISOString().split("T")[0];
    try {
      await api.post(`/settings/section-period`, { 
        section, 
        start_date: today 
      });
      updateSectionPeriod(section, today);
      toast.success(`Nuovo periodo per ${SECTION_LABELS[section]} impostato da oggi`);
      setOpen(false);
      refetchSettings();
      if (onPeriodSet) onPeriodSet();
    } catch (error) {
      toast.error("Errore: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="new-period-btn">
          <Calendar className="w-4 h-4 mr-1" /> Nuovo periodo
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            Nuovo periodo per {SECTION_LABELS[section]}
          </DialogTitle>
          <DialogDescription>
            I conteggi e i grafici partiranno da oggi, ma i dati precedenti restano salvati.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 p-4 bg-muted/50 rounded-sm">
          <p className="text-sm">✅ I grafici mostreranno dati <strong>da oggi in poi</strong></p>
          <p className="text-sm">✅ I dati storici restano <strong>salvati in memoria</strong></p>
          <p className="text-sm">✅ Potrai vedere lo storico con "Mostra tutto"</p>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
            Annulla
          </Button>
          <Button 
            onClick={handleSetPeriod} 
            disabled={loading}
            className="rounded-sm"
            data-testid="confirm-new-period-btn"
          >
            {loading ? "Impostazione..." : "Inizia nuovo periodo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Reset section (delete all) component
export const ResetSectionButton = ({ section, onReset }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleReset = async () => {
    if (confirmText !== "ELIMINA") {
      toast.error("Scrivi ELIMINA per confermare");
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/settings/reset-section`, { section });
      toast.success(`${SECTION_LABELS[section]} resettato completamente`);
      setOpen(false);
      setConfirmText("");
      if (onReset) onReset();
    } catch (error) {
      toast.error("Errore: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="reset-section-btn">
          <AlertTriangle className="w-4 h-4 mr-1" /> Reset completo
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Reset completo - {SECTION_LABELS[section]}
          </DialogTitle>
          <DialogDescription>
            Stai per eliminare <strong>TUTTI</strong> i dati di {SECTION_LABELS[section]}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-sm text-destructive font-medium">⚠️ ATTENZIONE: Azione irreversibile!</p>
          <p className="text-sm text-muted-foreground mt-1">
            Tutti i dati di questa sezione verranno eliminati permanentemente.
          </p>
        </div>
        
        <div>
          <label className="text-sm text-muted-foreground">Scrivi <strong>ELIMINA</strong> per confermare:</label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-border rounded-sm text-sm"
            placeholder="ELIMINA"
          />
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
            Annulla
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleReset} 
            disabled={loading || confirmText !== "ELIMINA"}
            className="rounded-sm"
            data-testid="confirm-reset-btn"
          >
            {loading ? "Eliminazione..." : "Elimina tutto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Show all history toggle
export const ShowHistoryToggle = ({ showAll, onToggle, sectionPeriod }) => {
  if (!sectionPeriod) return null;
  
  return (
    <Button
      variant={showAll ? "default" : "outline"}
      size="sm"
      onClick={() => onToggle(!showAll)}
      className="rounded-sm"
      data-testid="show-history-toggle"
    >
      <History className="w-4 h-4 mr-1" />
      {showAll ? "Mostra periodo attuale" : "Mostra tutto lo storico"}
    </Button>
  );
};

// Section actions toolbar - combines all actions
export const SectionActionsToolbar = ({ 
  section, 
  currentDate, 
  showAll, 
  onShowAllChange,
  onDataChanged 
}) => {
  const { getSectionPeriod } = usePeriod();
  const sectionPeriod = getSectionPeriod(section);

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/30 border border-border rounded-sm">
      <span className="text-sm text-muted-foreground mr-2">Azioni:</span>
      
      {currentDate && (
        <DeleteDayButton 
          section={section} 
          date={currentDate} 
          onDeleted={onDataChanged} 
        />
      )}
      
      <NewPeriodButton 
        section={section} 
        onPeriodSet={onDataChanged} 
      />
      
      <ShowHistoryToggle 
        showAll={showAll} 
        onToggle={onShowAllChange} 
        sectionPeriod={sectionPeriod}
      />
      
      <div className="ml-auto">
        <ResetSectionButton 
          section={section} 
          onReset={onDataChanged} 
        />
      </div>
    </div>
  );
};

export default SectionActionsToolbar;
